import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useAdminAuth } from '../hooks/useAdminAuth';
import {
  safetyService, type SafetyReportAction, type SafetyReportDetail,
  type SafetyReportStatus, type SafetyReportSummary,
} from '../services/safetyService';

const label = (value: string) => value.replaceAll('_', ' ');
const dateLabel = (value: string) => new Date(value).toLocaleString();
const visibleProfileFields = ['name', 'title', 'company', 'location', 'bio', 'avatar_url', 'linkedin', 'portfolio'];
const queuePageSize = 200;

const actionLabels: { action: SafetyReportAction; label: string }[] = [
  { action: 'start_review', label: 'Start review' },
  { action: 'resolve', label: 'Resolve' },
  { action: 'dismiss', label: 'Dismiss' },
  { action: 'hide_profile', label: 'Hide profile from directory' },
  { action: 'deactivate_registration', label: 'Remove from event' },
  { action: 'suspend_account', label: 'Suspend account' },
];

function availableActions(report: SafetyReportDetail, platformAdmin: boolean) {
  if (report.status === 'resolved' || report.status === 'dismissed') return [];
  return actionLabels.filter(({ action }) => {
    if (action === 'start_review') return report.status === 'open';
    if (action === 'hide_profile' || action === 'deactivate_registration') {
      return report.subject_type === 'attendee_profile';
    }
    if (action === 'suspend_account') {
      return platformAdmin && report.subject_type === 'attendee_profile';
    }
    return true;
  });
}

export default function SafetyReportsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { isPlatformAdmin } = useAdminAuth();
  const [status, setStatus] = useState<SafetyReportStatus | ''>('');
  const [reports, setReports] = useState<SafetyReportSummary[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<SafetyReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [pendingSuspend, setPendingSuspend] = useState(false);
  const scopeGeneration = useRef(0);
  const detailGeneration = useRef(0);

  const scope = eventId ? { eventId } : {};
  useEffect(() => {
    let active = true;
    scopeGeneration.current += 1;
    detailGeneration.current += 1;
    setReports([]);
    setPendingCount(0);
    setHasMore(false);
    setLoadingMore(false);
    setSelected(null);
    setDetailLoading(false);
    setPendingSuspend(false);
    setNote('');
    setError('');
    setLoading(true);
    Promise.all([
      safetyService.list({ ...scope, ...(status ? { status } : {}) }),
      safetyService.pendingCount(eventId),
    ])
      .then(([items, pending]) => {
        if (!active) return;
        setReports(items);
        setHasMore(items.length === queuePageSize);
        setPendingCount(pending.count);
      })
      .catch((failure) => { if (active) setError(failure instanceof Error ? failure.message : 'Could not load reports.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [eventId, status]);

  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    const scopeVersion = scopeGeneration.current;
    setLoadingMore(true);
    setError('');
    try {
      const older = await safetyService.list({ ...scope, ...(status ? { status } : {}), offset: reports.length });
      if (scopeVersion !== scopeGeneration.current) return;
      setReports((current) => [...current, ...older]);
      setHasMore(older.length === queuePageSize);
    } catch (failure) {
      if (scopeVersion === scopeGeneration.current) {
        setError(failure instanceof Error ? failure.message : 'Could not load older reports.');
      }
    } finally {
      if (scopeVersion === scopeGeneration.current) setLoadingMore(false);
    }
  };

  const openReport = async (reportId: string) => {
    const scopeVersion = scopeGeneration.current;
    const detailVersion = ++detailGeneration.current;
    setError('');
    setDetailLoading(true);
    setPendingSuspend(false);
    try {
      const result = await safetyService.detail(reportId);
      if (scopeVersion !== scopeGeneration.current || detailVersion !== detailGeneration.current) return;
      setSelected(result);
      setNote('');
    } catch (failure) {
      if (scopeVersion === scopeGeneration.current && detailVersion === detailGeneration.current) {
        setError(failure instanceof Error ? failure.message : 'Could not load report.');
      }
    } finally {
      if (scopeVersion === scopeGeneration.current && detailVersion === detailGeneration.current) {
        setDetailLoading(false);
      }
    }
  };

  const applyAction = async (action: SafetyReportAction) => {
    if (!selected || acting || (eventId && selected.event_id !== eventId)) return;
    if (action === 'suspend_account' && !pendingSuspend) {
      setPendingSuspend(true);
      return;
    }
    setError('');
    setActing(true);
    const scopeVersion = scopeGeneration.current;
    const reportId = selected.id;
    try {
      const updated = await safetyService.act(reportId, action, note);
      if (scopeVersion !== scopeGeneration.current) return;
      setSelected(updated);
      setNote('');
      setPendingSuspend(false);
      setReports((current) => current.map((report) => report.id === reportId ? updated : report));
      try {
        const [refreshed, pending] = await Promise.all([
          safetyService.list({ ...scope, ...(status ? { status } : {}) }),
          safetyService.pendingCount(eventId),
        ]);
        if (scopeVersion === scopeGeneration.current) {
          setReports(refreshed);
          setHasMore(refreshed.length === queuePageSize);
          setPendingCount(pending.count);
        }
      } catch {
        if (scopeVersion === scopeGeneration.current) {
          setError('Action applied, but the queue could not refresh. Reload to see the latest queue.');
        }
      }
    } catch (failure) {
      if (scopeVersion === scopeGeneration.current) {
        setError(failure instanceof Error ? failure.message : 'Could not apply action.');
      }
    } finally {
      setActing(false);
    }
  };

  const visibleReports = eventId ? reports.filter((report) => report.event_id === eventId) : reports;
  const visibleSelected = eventId && selected?.event_id !== eventId ? null : selected;
  const profile = visibleSelected?.evidence.profile;

  return (
    <div className="mx-auto max-w-6xl p-8 text-gray-100">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Safety reports</h1>
        <p className="mt-1 text-sm text-gray-400">
          {eventId ? 'Review attendee profile reports for this event.' : 'Review platform-wide attendee and AI reports.'}
        </p>
        <p className="mt-2 text-sm text-amber-200">{pendingCount} awaiting resolution</p>
      </div>
      <label className="mb-5 block max-w-xs text-sm text-gray-300">
        Status
        <select
          aria-label="Report status"
          value={status}
          onChange={(change) => setStatus(change.target.value as SafetyReportStatus | '')}
          className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-white"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_review">In review</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </label>
      {error && <p role="alert" className="mb-4 rounded-lg border border-red-900 bg-red-950 p-3 text-sm text-red-200">{error}</p>}
      <div className="grid gap-5 lg:grid-cols-[minmax(16rem,1fr)_minmax(22rem,1.5fr)]">
        <section aria-label="Report queue" className="rounded-xl border border-gray-800 bg-gray-900 p-4">
          {loading ? <p className="text-sm text-gray-400">Loading reports…</p>
            : visibleReports.length === 0 ? <p className="text-sm text-gray-400">No reports in this view.</p>
              : <div className="space-y-2">{visibleReports.map((report) => (
                <button
                  key={report.id}
                  type="button"
                  onClick={() => void openReport(report.id)}
                  className={`w-full rounded-lg border p-3 text-left text-sm ${visibleSelected?.id === report.id ? 'border-sky-500 bg-sky-500/10' : 'border-gray-800 bg-gray-950 hover:border-gray-600'}`}
                >
                  <span className="block font-medium capitalize">{label(report.category)}</span>
                  <span className="mt-1 block text-xs capitalize text-gray-400">{label(report.subject_type)} · {label(report.status)}</span>
                  <span className="mt-1 block text-xs text-gray-500">{dateLabel(report.created_at)}</span>
                </button>
              ))}
                {hasMore && <button type="button" onClick={() => void loadMore()} disabled={loadingMore}
                  className="w-full rounded-lg border border-gray-700 px-3 py-2 text-sm text-sky-300 disabled:opacity-50">
                  {loadingMore ? 'Loading older reports…' : 'Load older reports'}
                </button>}
              </div>}
        </section>
        <section aria-label="Report details" className="rounded-xl border border-gray-800 bg-gray-900 p-5">
          {detailLoading ? <p className="text-sm text-gray-400">Loading report…</p>
            : !visibleSelected ? <p className="text-sm text-gray-400">Select a report to review its evidence and history.</p>
              : <div className="space-y-5">
                <div>
                  <h2 className="text-lg font-semibold capitalize">{label(visibleSelected.category)}</h2>
                  <p className="text-xs text-gray-400">{label(visibleSelected.subject_type)} · {label(visibleSelected.status)} · {dateLabel(visibleSelected.created_at)}</p>
                  {visibleSelected.event_id && <p className="mt-1 text-xs text-gray-500">Event {visibleSelected.event_id}</p>}
                </div>
                {visibleSelected.details && <div><h3 className="text-sm font-medium">Reporter context</h3><p className="mt-1 whitespace-pre-wrap text-sm text-gray-300">{visibleSelected.details}</p></div>}
                <div>
                  <h3 className="text-sm font-medium">Captured evidence</h3>
                  {profile ? <dl className="mt-2 space-y-2 text-sm">{visibleProfileFields.filter((key) => profile[key]).map((key) => (
                    <div key={key}><dt className="text-xs capitalize text-gray-500">{label(key)}</dt><dd className="whitespace-pre-wrap break-words text-gray-300">{profile[key]}</dd></div>
                  ))}</dl> : <div className="mt-2 space-y-3 text-sm text-gray-300">
                    {visibleSelected.evidence.preceding_prompt && <div><p className="text-xs text-gray-500">Preceding prompt</p><p className="whitespace-pre-wrap">{visibleSelected.evidence.preceding_prompt}</p></div>}
                    {visibleSelected.evidence.response && <div><p className="text-xs text-gray-500">AI response</p><p className="whitespace-pre-wrap">{visibleSelected.evidence.response}</p></div>}
                    {visibleSelected.evidence.route && <p className="text-xs text-gray-500">Source: {label(visibleSelected.evidence.route)}</p>}
                  </div>}
                </div>
                <div><h3 className="text-sm font-medium">Action history</h3>
                  {visibleSelected.actions.length ? <ol className="mt-2 space-y-2 text-xs text-gray-300">{visibleSelected.actions.map((action) => (
                    <li key={action.id} className="rounded border border-gray-800 p-2">
                      <span className="capitalize">{label(action.action)}</span> · {dateLabel(action.created_at)}
                      {action.note && <p className="mt-1 whitespace-pre-wrap">{action.note}</p>}
                    </li>
                  ))}</ol> : <p className="mt-1 text-xs text-gray-500">No actions yet.</p>}
                </div>
                {availableActions(visibleSelected, isPlatformAdmin).length > 0 && <div className="border-t border-gray-800 pt-4">
                  <label className="block text-sm text-gray-300">Reviewer note (optional)
                    <textarea value={note} onChange={(change) => setNote(change.target.value)} maxLength={2000} rows={3}
                      className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white" />
                  </label>
                  <div className="mt-3 flex flex-wrap gap-2">{availableActions(visibleSelected, isPlatformAdmin).map(({ action, label: actionLabel }) => (
                    <button key={action} type="button" disabled={acting} onClick={() => void applyAction(action)}
                      className={`rounded-lg px-3 py-2 text-xs font-medium disabled:opacity-50 ${action === 'suspend_account' ? 'bg-red-900 text-white' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
                    >{actionLabel}</button>
                  ))}</div>
                  {pendingSuspend && <div className="mt-3 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-100">
                    Suspend this account across the platform? This will immediately block sign-in.
                    <div className="mt-2 flex gap-2">
                      <button type="button" onClick={() => void applyAction('suspend_account')} disabled={acting} className="rounded bg-red-700 px-3 py-1">Confirm suspension</button>
                      <button type="button" onClick={() => setPendingSuspend(false)} className="rounded bg-gray-800 px-3 py-1">Cancel</button>
                    </div>
                  </div>}
                </div>}
              </div>}
        </section>
      </div>
    </div>
  );
}
